/**
 * Wheel Cell Timeline
 *
 * Shows individual occurrences that match the currently selected Pattern Wheel
 * cell, plotted as dots over the date range of the data.
 *
 * - Row 1: Dwells + Route Trips (location/movement events)   [client-side data]
 * - Row 2: Digital (calls + SMS + apps + browsing)           [fetched per-cell]
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography, Tooltip, CircularProgress } from '@mui/material';
import { TimelineDwell, RoutePattern, polService, HourlyEvent } from '../services/polService';

interface WheelCellTimelineProps {
  dwells: TimelineDwell[];
  routes: RoutePattern[];
  hour: number;
  day: number;
  // Context for digital event fetch
  courseId?: string;
  daysBack?: number;
  entityIds?: string; // comma-separated, optional filter
  // Explicit period passed to the hourly-detail endpoint so it matches the
  // dashboard's custom date range (otherwise the endpoint falls back to
  // `days_back` relative to now, which produces zero results when the user
  // has selected a historical window).
  startDate?: string; // YYYY-MM-DD
  endDate?: string;   // YYYY-MM-DD
  // Period bounds (as Date objects) used to filter local dwell/route data so
  // counts match the KPI cards and wheel exactly.
  periodStart?: Date;
  periodEnd?: Date;
  // Heatmap cell totals — drives the Digital row header and lets us show the
  // row even when the fetch returns zero events (e.g. response cached, row
  // limit clipped, or request in flight).
  expectedDigitalTotal?: number;
}

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

type Occurrence = {
  id: string;
  timestamp: number;
  rowKey: 'location' | 'digital';
  subKind: 'dwell' | 'route' | 'call' | 'sms' | 'app' | 'browsing';
  label: string;
  entity_id: string;
  detail: string;
  duration_seconds?: number;
};

const KIND_META: Record<Occurrence['subKind'], { color: string; label: string }> = {
  dwell:    { color: '#f59e0b', label: 'Dwell' },
  route:    { color: '#a371f7', label: 'Route' },
  call:     { color: '#f85149', label: 'Call' },
  sms:      { color: '#ec4899', label: 'SMS' },
  app:      { color: '#3fb950', label: 'App' },
  browsing: { color: '#58a6ff', label: 'Browsing' },
};

const formatDuration = (seconds: number) => {
  if (!seconds || seconds <= 0) return '';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
};

const formatDateTime = (ms: number) =>
  new Date(ms).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });

const WheelCellTimeline: React.FC<WheelCellTimelineProps> = ({
  dwells, routes, hour, day, courseId, daysBack = 30, entityIds,
  startDate, endDate, periodStart, periodEnd, expectedDigitalTotal = 0,
}) => {
  const periodStartMs = periodStart ? periodStart.getTime() : -Infinity;
  const periodEndMs = periodEnd ? periodEnd.getTime() : Infinity;
  const [hover, setHover] = useState<Occurrence | null>(null);
  const [digitalEvents, setDigitalEvents] = useState<HourlyEvent[]>([]);
  const [digitalLoading, setDigitalLoading] = useState(false);
  const [digitalSummary, setDigitalSummary] = useState<{ calls: number; sms: number; apps: number; browsing: number } | null>(null);

  // Fetch digital events for the selected cell (calls/sms/apps/browsing)
  useEffect(() => {
    let cancelled = false;
    setDigitalLoading(true);
    setDigitalEvents([]);
    setDigitalSummary(null);
    polService.getHourlyDetail(day, hour, courseId, daysBack, entityIds, 200, startDate, endDate)
      .then(resp => {
        if (cancelled) return;
        setDigitalEvents(resp.events || []);
        setDigitalSummary(resp.summary || null);
      })
      .catch(err => {
        if (!cancelled) {
          console.warn('[WheelCellTimeline] digital fetch failed:', err);
        }
      })
      .finally(() => { if (!cancelled) setDigitalLoading(false); });
    return () => { cancelled = true; };
  }, [day, hour, courseId, daysBack, entityIds, startDate, endDate]);

  // Location row: dwells + route trips filtered to this cell.
  // Filtering uses the same fields the backend heatmap groups by (UTC hour/DOW
  // extracted server-side maps to JS getHours/getDay when parsing naive-UTC
  // strings stored by the ingestion pipeline), and respects the active period
  // window so counts line up with the KPI cards.
  const locationOccurrences = useMemo<Occurrence[]>(() => {
    const occ: Occurrence[] = [];
    dwells.forEach((d) => {
      if (!d.start_time) return;
      const dt = new Date(d.start_time);
      const ts = dt.getTime();
      if (ts < periodStartMs || ts >= periodEndMs) return;
      if (dt.getHours() !== hour || dt.getDay() !== day) return;
      occ.push({
        id: `d-${d.dwell_id}`,
        timestamp: ts,
        rowKey: 'location',
        subKind: 'dwell',
        label: `Dwell · ${d.entity_id}`,
        entity_id: d.entity_id,
        detail: `${d.device_type} · ${d.location_name || `${d.lat?.toFixed(4)},${d.lon?.toFixed(4)}`}`,
        duration_seconds: d.duration_seconds,
      });
    });
    routes.forEach((r) => {
      (r.trip_instances || []).forEach((t, idx) => {
        if (!t.timestamp) return;
        const dt = new Date(t.timestamp);
        const ts = dt.getTime();
        if (ts < periodStartMs || ts >= periodEndMs) return;
        if (dt.getHours() !== hour || dt.getDay() !== day) return;
        occ.push({
          id: `r-${r.id}-${idx}`,
          timestamp: ts,
          rowKey: 'location',
          subKind: 'route',
          label: `Route · ${r.entity_id}`,
          entity_id: r.entity_id,
          detail: r.route_name || `${r.origin?.name ?? '?'} → ${r.destination?.name ?? '?'}`,
          duration_seconds: t.duration_s,
        });
      });
    });
    return occ;
  }, [dwells, routes, hour, day, periodStartMs, periodEndMs]);

  // Digital row: events from API
  const digitalOccurrences = useMemo<Occurrence[]>(() => {
    return digitalEvents
      .filter(e => !!e.timestamp)
      .map((e, idx) => {
        const ts = new Date(e.timestamp as string).getTime();
        const subKind: Occurrence['subKind'] =
          e.type === 'call' ? 'call' :
          e.type === 'sms' ? 'sms' :
          e.type === 'app' ? 'app' : 'browsing';
        return {
          id: `${subKind}-${idx}-${ts}`,
          timestamp: ts,
          rowKey: 'digital',
          subKind,
          label: `${KIND_META[subKind].label} · ${e.entity_id}`,
          entity_id: e.entity_id,
          detail: e.contact ? `${e.detail} — ${e.contact}` : e.detail,
        };
      });
  }, [digitalEvents]);

  const allOccurrences = useMemo(() => [...locationOccurrences, ...digitalOccurrences], [locationOccurrences, digitalOccurrences]);

  const { dateMin, dateMax } = useMemo(() => {
    if (allOccurrences.length === 0) return { dateMin: Date.now(), dateMax: Date.now() };
    const ts = allOccurrences.map(o => o.timestamp);
    return { dateMin: Math.min(...ts), dateMax: Math.max(...ts) };
  }, [allOccurrences]);

  // Count summary for header
  const totalLocation = locationOccurrences.length;
  const totalDigital = digitalOccurrences.length;

  if (allOccurrences.length === 0 && !digitalLoading && expectedDigitalTotal === 0) {
    return (
      <Box sx={{ mt: 1.5, p: 1.5, borderRadius: 1, bgcolor: 'rgba(255,255,255,0.03)', border: '1px solid', borderColor: 'divider', textAlign: 'center' }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
          No events for {DAY_NAMES[day]} {hour.toString().padStart(2, '0')}:00 in the current view.
        </Typography>
      </Box>
    );
  }

  const range = Math.max(dateMax - dateMin, 60 * 60 * 1000);
  const paddedMin = dateMin - range * 0.02;
  const paddedMax = dateMax + range * 0.02;
  const paddedRange = paddedMax - paddedMin;
  const xPct = (ts: number) => ((ts - paddedMin) / paddedRange) * 100;

  const maxDuration = Math.max(1, ...allOccurrences.map(o => o.duration_seconds || 0));

  const tickCount = 5;
  const ticks = Array.from({ length: tickCount }, (_, i) => {
    const ts = paddedMin + (paddedRange * i) / (tickCount - 1);
    return { ts, pct: (i / (tickCount - 1)) * 100 };
  });

  const ROW_H = 24;
  const ROW_LABEL_W = 72;

  type Row = {
    key: 'location' | 'digital';
    label: string;
    count: number;
  };
  const rows: Row[] = [];
  if (totalLocation > 0) {
    rows.push({ key: 'location', label: 'Location', count: totalLocation });
  }
  if (totalDigital > 0 || digitalLoading || expectedDigitalTotal > 0) {
    rows.push({ key: 'digital', label: 'Digital', count: totalDigital });
  }

  // Shared legend counts across all rows (for bottom legend)
  const legendItems: { color: string; label: string; n: number }[] = [
    { color: KIND_META.dwell.color,    label: 'Dwells',   n: locationOccurrences.filter(o => o.subKind === 'dwell').length },
    { color: KIND_META.route.color,    label: 'Routes',   n: locationOccurrences.filter(o => o.subKind === 'route').length },
    { color: KIND_META.call.color,     label: 'Calls',    n: digitalOccurrences.filter(o => o.subKind === 'call').length },
    { color: KIND_META.sms.color,      label: 'SMS',      n: digitalOccurrences.filter(o => o.subKind === 'sms').length },
    { color: KIND_META.app.color,      label: 'Apps',     n: digitalOccurrences.filter(o => o.subKind === 'app').length },
    { color: KIND_META.browsing.color, label: 'Browsing', n: digitalOccurrences.filter(o => o.subKind === 'browsing').length },
  ].filter(l => l.n > 0);

  // Whether the digital summary reports more events than we fetched (clipped by limit)
  const digitalTotalReported = digitalSummary
    ? digitalSummary.calls + digitalSummary.sms + digitalSummary.apps + digitalSummary.browsing
    : 0;
  const digitalClipped = digitalTotalReported > digitalOccurrences.length;
  // True when the wheel/KPI promised digital events for this cell but the
  // per-event fetch returned none (e.g. events exist in the window but have
  // NULL device_reference_id, or the raw rows are outside the requested
  // time range). Surfacing this avoids the "row silently vanished" bug.
  const digitalMissingDetail = !digitalLoading && digitalOccurrences.length === 0 && expectedDigitalTotal > 0;

  return (
    <Box sx={{ mt: 1.5, p: 1.5, borderRadius: 1, bgcolor: 'rgba(255,255,255,0.03)', border: '1px solid', borderColor: 'divider' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: 1, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="subtitle2" sx={{ fontSize: '0.8rem', fontWeight: 700 }}>
          Occurrence Timeline
          <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.secondary', fontSize: '0.7rem' }}>
            {DAY_NAMES[day]} {hour.toString().padStart(2, '0')}:00 ·{' '}
            {allOccurrences.length.toLocaleString()} event{allOccurrences.length !== 1 ? 's' : ''}
            {digitalClipped ? ` (digital sample)` : ''}
          </Typography>
        </Typography>
        <Typography variant="caption" sx={{ fontSize: '0.6rem', color: 'text.secondary' }}>
          dot opacity = duration (where known)
        </Typography>
      </Box>

      {/* Rows */}
      <Box sx={{ position: 'relative' }}>
        {rows.map((row) => (
          <Box key={row.key} sx={{ position: 'relative', display: 'flex', alignItems: 'center', height: ROW_H, mb: 0.25 }}>
            {/* Row label */}
            <Box sx={{ width: ROW_LABEL_W, flexShrink: 0, display: 'flex', alignItems: 'center', gap: 0.5, pr: 0.5 }}>
              <Typography variant="caption" sx={{ fontSize: '0.65rem', fontWeight: 700 }}>
                {row.label}
              </Typography>
              <Typography variant="caption" sx={{ fontSize: '0.55rem', color: 'text.secondary' }}>
                ({row.key === 'digital' && digitalMissingDetail
                  ? `~${expectedDigitalTotal}`
                  : `${row.count}${row.key === 'digital' && digitalClipped ? `/${digitalTotalReported}` : ''}`})
              </Typography>
              {row.key === 'digital' && digitalLoading && <CircularProgress size={8} sx={{ ml: 0.25 }} />}
            </Box>

            {/* Dot track */}
            <Box sx={{
              position: 'relative',
              flex: 1,
              height: ROW_H,
              borderRadius: 0.5,
              bgcolor: 'rgba(255,255,255,0.02)',
              borderBottom: '1px dashed rgba(255,255,255,0.08)',
            }}>
              {row.key === 'digital' && digitalMissingDetail && (
                <Typography variant="caption" sx={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.55rem',
                  color: 'text.secondary',
                  opacity: 0.7,
                  fontStyle: 'italic',
                }}>
                  {expectedDigitalTotal} event{expectedDigitalTotal !== 1 ? 's' : ''} in this cell · per-event timestamps unavailable
                </Typography>
              )}
              {allOccurrences.filter(o => o.rowKey === row.key).map((o) => {
                const color = KIND_META[o.subKind].color;
                const opacity = o.duration_seconds
                  ? 0.35 + 0.65 * Math.min(1, (o.duration_seconds || 0) / maxDuration)
                  : 0.75;
                const isHover = hover?.id === o.id;
                return (
                  <Tooltip
                    key={o.id}
                    arrow
                    placement="top"
                    title={
                      <Box sx={{ fontSize: '0.7rem' }}>
                        <b>{o.label}</b><br />
                        {formatDateTime(o.timestamp)}<br />
                        {o.detail}
                        {o.duration_seconds ? <><br />{formatDuration(o.duration_seconds)}</> : null}
                      </Box>
                    }
                  >
                    <Box
                      onMouseEnter={() => setHover(o)}
                      onMouseLeave={() => setHover(null)}
                      sx={{
                        position: 'absolute',
                        left: `${xPct(o.timestamp)}%`,
                        top: '50%',
                        transform: `translate(-50%, -50%) scale(${isHover ? 1.6 : 1})`,
                        width: 9,
                        height: 9,
                        borderRadius: '50%',
                        bgcolor: color,
                        opacity,
                        border: isHover ? '1.5px solid #fff' : '1px solid rgba(0,0,0,0.4)',
                        cursor: 'pointer',
                        transition: 'transform 0.12s',
                      }}
                    />
                  </Tooltip>
                );
              })}
            </Box>
          </Box>
        ))}

        {/* Date tick labels */}
        <Box sx={{ position: 'relative', ml: `${ROW_LABEL_W}px`, mt: 0.25, height: 12 }}>
          {ticks.map((t, i) => (
            <Typography
              key={i}
              variant="caption"
              sx={{
                position: 'absolute',
                left: `${t.pct}%`,
                transform: i === 0 ? 'translateX(0)' : i === ticks.length - 1 ? 'translateX(-100%)' : 'translateX(-50%)',
                fontSize: '0.55rem',
                color: 'text.secondary',
                whiteSpace: 'nowrap',
              }}
            >
              {new Date(t.ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
            </Typography>
          ))}
        </Box>
      </Box>

      {/* Bottom legend (shared across rows) */}
      {legendItems.length > 0 && (
        <Box sx={{
          mt: 1,
          pt: 0.75,
          borderTop: '1px dashed rgba(255,255,255,0.08)',
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'center',
          gap: 1.25,
          rowGap: 0.5,
        }}>
          {legendItems.map((l, i) => (
            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: l.color }} />
              <Typography variant="caption" sx={{ fontSize: '0.62rem', color: 'text.secondary', lineHeight: 1 }}>
                {l.label}
                <Typography component="span" sx={{ ml: 0.4, fontSize: '0.58rem', opacity: 0.7 }}>
                  {l.n}
                </Typography>
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default WheelCellTimeline;
